import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertTriangle, BookOpen, CalendarDays, Clock, Eye, FileText, Heart, History, House, Paperclip, RefreshCw, User, Users, X } from "lucide-react";
import { getSharedSchedule, sharedScheduleAttachmentUrl } from "../api";
import { STATUS_LABEL, planRowsFromText } from "./DailyScheduleVisitModals";
import { ampm, fromKey, hhmm, longDate } from "./dailyScheduleShared";
import { LEARNING_COLUMNS } from "./lessonPlanColumns";
import { FixedLearning, FixedValue, PlanTableHead, fixedAppMeta, rowLearning } from "./lessonPlanCells";
import "./AdminDailySchedule.css";
import "./SharedSchedulePage.css";

// /daily-schedule/shared?date=2026-10-15&balagruha=Snehchaya&key=… — opened from a visit shared on the
// admin Daily Schedule (Share visit). No login: the key only opens that Balgruha on that date. Read-only,
// laid out like the Create Lesson Plan page (/daily-schedule/create).

// "2026-10-15", and a real day (so longDate can format it).
const isShareDateKey = (value) => /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(fromKey(value).getTime());
const MEMBER_STATUS = { pending: "Scheduled", inprogress: "In progress", completed: "Completed", cancelled: "Cancelled" };
const timeRange = (s) => `${ampm(hhmm(s.startTime))} – ${ampm(hhmm(s.endTime))}`;
const rowsOf = (s) => (s.lessonPlan?.children?.length ? s.lessonPlan.children : planRowsFromText(s.description));

// Same grouping as a visit card on the Daily Schedule: cancelled coaches drop out unless all are cancelled.
const visitOf = (schedules) => {
  const live = schedules.filter((s) => s.status !== "cancelled");
  const members = live.length ? live : schedules;
  const status = !live.length ? "cancelled" : live.every((s) => s.status === "completed") ? "completed" : "active";
  const endTime = members.reduce((end, s) => (new Date(s.endTime) > new Date(end) ? s.endTime : end), members[0].endTime);
  return { members, status, startTime: members[0].startTime, endTime };
};

// Every coach has their own copy of the plan; copies that match are shown once.
const planGroups = (members) => {
  const groups = new Map();
  members.forEach((s) => {
    const rows = rowsOf(s);
    const key = JSON.stringify([
      rows, s.learningsFocus || s.lessonPlan?.course || "", s.healthCheck, s.specialInstructions,
      rows.length ? "" : s.description, (s.attachments || []).map((a) => [a.filename, a.size]),
    ]);
    if (groups.has(key)) groups.get(key).members.push(s);
    else groups.set(key, { key, rows, first: s, members: [s] });
  });
  return [...groups.values()];
};

// "@Asha has a cough" / "@all" highlighted, as on the coach's Daily Schedule.
function TaggedText({ text, names }) {
  const escaped = names.filter(Boolean).sort((a, b) => b.length - a.length).map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  escaped.push("all(?![\\w])");
  return text.split(new RegExp(`(@(?:${escaped.join("|")}))`, "g")).map((part, i) => (i % 2 ? <mark key={i}>{part}</mark> : part));
}

function Field({ label, icon, children }) {
  return (
    <div className="ds-field">
      <span>{label}</span>
      <div className="ss-box">{icon}<span>{children}</span></div>
    </div>
  );
}

function Message({ icon, title, children }) {
  return (
    <section className="ss-message" role="status">
      {icon}
      <h2>{title}</h2>
      {children}
    </section>
  );
}

function PlanCard({ group, centreName, showCoaches, date, shareKey }) {
  const { rows, first, members } = group;
  const focus = first.learningsFocus || first.lessonPlan?.course;
  const names = rows.map((r) => r.childName);
  return (
    <section className="lp-card ss-card">
      <div className="lp-card__head">
        <div className="lp-card__title">
          <h2>Lesson Plan — {centreName}</h2>
          {rows.length > 0 && <span className="lp-count">{rows.length} {rows.length === 1 ? "Child" : "Children"}</span>}
        </div>
        {focus && <p className="lp-hint"><BookOpen size={15} color="#1d4ed8" />Learning focus: <b>{focus}</b></p>}
      </div>
      {showCoaches && members.map((s) => (
        <p key={s._id} className="ss-coach"><User size={15} /><b>{s.coachName || "Coach"}</b><span>· {timeRange(s)} · {MEMBER_STATUS[s.status] || "Scheduled"}</span></p>
      ))}

      {rows.length > 0 ? (
        <div className="lp-table-wrap">
          {/* The Create Schedule table, read-only (as on /daily-schedule/view). */}
          <table className="lp-table lp-table--plan ss-table">
            <PlanTableHead />
            <tbody>
              {rows.map((r, i) => {
                const learning = rowLearning(r);
                return (
                  <tr key={`${r.childName}-${i}`}>
                    <td data-label="#">{i + 1}</td>
                    <td data-label="Child Name"><b>{r.childName}</b></td>
                    <td data-label="Computer Apps" title={r.computer || undefined}><FixedValue value={r.computer} meta={fixedAppMeta} /></td>
                    {LEARNING_COLUMNS.map(({ key, label }) => (
                      <td key={key} data-label={label} title={learning[key] || undefined} className={learning[key] ? undefined : "ss-cell--empty"}>
                        <FixedLearning column={key} value={learning[key]} />
                      </td>
                    ))}
                    <td data-label="Special Instructions" title={r.note || undefined}>
                      {r.note ? <span className="lp-input lp-input--fixed">{r.note}</span> : <span className="lp-fixed-empty">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : first.description ? (
        <p className="ss-text">{first.description}</p>
      ) : (
        <p className="ss-text ss-text--empty">The admin hasn&apos;t added a child-wise lesson plan for this visit yet.</p>
      )}

      {(first.healthCheck || first.specialInstructions || first.attachments?.length > 0) && (
        <div className="ss-extras">
          {first.healthCheck && (
            <div>
              <h3><span className="lp-extras__icon is-pink"><Heart size={16} fill="currentColor" /></span>Health Check</h3>
              <p className="ss-text"><TaggedText text={first.healthCheck} names={names} /></p>
            </div>
          )}
          {first.specialInstructions && (
            <div>
              <h3><span className="lp-extras__icon is-orange"><FileText size={16} /></span>General Instructions</h3>
              <p className="ss-text">{first.specialInstructions}</p>
            </div>
          )}
          {first.attachments?.length > 0 && (
            <div>
              <h3><span className="lp-extras__icon"><Paperclip size={16} /></span>Attachments</h3>
              <ul className="ss-files">
                {first.attachments.map((a) => (
                  <li key={a._id}>
                    <a href={sharedScheduleAttachmentUrl({ scheduleId: first._id, attachmentId: a._id, date, key: shareKey })} target="_blank" rel="noopener noreferrer">
                      <Paperclip size={13} />{a.filename || "Attachment"}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

const coachNamesOf = (visit) => [...new Set(visit.members.map((s) => s.coachName).filter(Boolean))];

function VisitFields({ date, centreName, visit }) {
  const coaches = coachNamesOf(visit);
  return (
    <div className="lp-fields ss-fields">
      <Field label="Date" icon={<CalendarDays size={16} />}>{longDate(date)}</Field>
      <Field label="Balgruha" icon={<House size={16} fill="currentColor" />}>{centreName}</Field>
      <Field label="Coaches" icon={<Users size={17} fill="currentColor" />}>
        <span className="ss-chips">{coaches.length ? coaches.map((name) => <span key={name} className="lp-chip">{name}</span>) : "—"}</span>
      </Field>
      <Field label="Start Time" icon={<Clock size={16} />}>{ampm(hhmm(visit.startTime))}</Field>
      <Field label="End Time" icon={<Clock size={16} />}>{ampm(hhmm(visit.endTime))}</Field>
    </div>
  );
}

// The One-on-One columns a plan fills, in table order ("Intermediate Scenes, Action Songs"); else its focus.
const planSubjects = (groups) => {
  const rows = groups.flatMap((g) => g.rows);
  const used = LEARNING_COLUMNS.filter(({ key }) => rows.some((r) => rowLearning(r)[key])).map((c) => c.label);
  const focus = [...new Set(groups.map((g) => g.first.learningsFocus || g.first.lessonPlan?.course).filter(Boolean))];
  return (used.length ? used : focus).join(", ") || "—";
};

// The last visit, opened read-only from "Last Visit" > View. Its attachments open with this page's
// date and key (the server accepts the last visit's files too).
function LastVisitDialog({ last, centreName, date, shareKey, onClose }) {
  const closeRef = useRef(null);
  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="ss-modal" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="ss-modal__card" role="dialog" aria-modal="true" aria-labelledby="ss-last-title">
        <div className="ss-modal__head">
          <h2 id="ss-last-title"><History size={20} />Last Visit — {longDate(last.date)}</h2>
          <span className={`ds-status ds-status--${last.visit.status}`}>{STATUS_LABEL[last.visit.status] || last.visit.status}</span>
          <button ref={closeRef} type="button" aria-label="Close" onClick={onClose}><X size={20} /></button>
        </div>
        <VisitFields date={last.date} centreName={centreName} visit={last.visit} />
        {last.groups.map((group) => (
          <PlanCard key={group.key} group={group} centreName={centreName} showCoaches={last.groups.length > 1} date={date} shareKey={shareKey} />
        ))}
      </div>
    </div>
  );
}

// The Balgruha's visit before this one, as a row with a View button (like the coach's Previous Plans).
function LastVisit({ lastVisit, centreName, date, shareKey }) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const schedules = lastVisit?.schedules || [];
  const visit = schedules.length ? visitOf(schedules) : null;
  const groups = visit ? planGroups(visit.members) : [];
  const children = new Set(groups.flatMap((g) => g.rows.map((r) => r.childName))).size;
  return (
    <section className="ss-last" aria-labelledby="ss-last-heading">
      <h2 id="ss-last-heading"><History size={18} />Last Visit <small>(For Reference)</small></h2>
      {visit ? (
        <div className="ss-last__wrap">
          <table className="ss-last__table">
            <thead><tr><th>Date</th><th>Time</th><th>Coaches</th><th>Total Children</th><th>Category</th><th>Action</th></tr></thead>
            <tbody>
              <tr>
                <td data-label="Date">{longDate(lastVisit.date)}</td>
                <td data-label="Time">{timeRange(visit)}</td>
                <td data-label="Coaches">{coachNamesOf(visit).join(", ") || "—"}</td>
                <td data-label="Total Children">{children || "—"}</td>
                <td data-label="Category">{planSubjects(groups)}</td>
                <td data-label="Action">
                  <button type="button" className="ss-view" onClick={() => setOpen(true)} aria-label={`View the visit on ${longDate(lastVisit.date)}`}>
                    <Eye size={15} />View
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      ) : (
        <p className="ss-last__empty">No earlier visit to {centreName}.</p>
      )}
      {open && visit && <LastVisitDialog last={{ date: lastVisit.date, visit, groups }} centreName={centreName} date={date} shareKey={shareKey} onClose={close} />}
    </section>
  );
}

export default function SharedSchedulePage() {
  const [params] = useSearchParams();
  const date = params.get("date") || "";
  const shareKey = params.get("key") || "";
  const nameInLink = params.get("balagruha") || "";
  const validLink = isShareDateKey(date) && Boolean(shareKey);
  const [state, setState] = useState({ status: validLink ? "loading" : "invalid" });

  const load = useCallback(() => {
    if (!validLink) { setState({ status: "invalid" }); return () => {}; }
    let cancelled = false;
    setState({ status: "loading" });
    getSharedSchedule({ date, key: shareKey })
      .then((data) => { if (!cancelled) setState({ status: "ready", data }); })
      .catch((err) => {
        if (cancelled) return;
        const code = err?.response?.status;
        setState({ status: code === 403 || code === 404 || code === 400 ? "invalid" : "error" });
      });
    return () => { cancelled = true; };
  }, [validLink, date, shareKey]);

  useEffect(() => load(), [load]);

  const centreName = state.data?.balagruha?.name || nameInLink || "Balgruha";

  // The page is only for people the link was sent to.
  useEffect(() => {
    const previousTitle = document.title;
    document.title = `Schedule — ${centreName}${isShareDateKey(date) ? `, ${longDate(date)}` : ""}`;
    const robots = document.createElement("meta");
    robots.name = "robots";
    robots.content = "noindex, nofollow";
    document.head.appendChild(robots);
    return () => { document.title = previousTitle; robots.remove(); };
  }, [centreName, date]);

  const schedules = state.data?.schedules || [];
  const visit = schedules.length ? visitOf(schedules) : null;
  const groups = visit ? planGroups(visit.members) : [];
  // Sent by the server since "Last Visit" was added; null when the Balgruha had no earlier visit.
  const hasLastVisit = state.status === "ready" && state.data && "lastVisit" in state.data;
  const lastVisit = hasLastVisit ? state.data.lastVisit : null;

  return (
    <main className="ds-page ss-page">
      <div className="lp-main ss-main">
        <header className="lp-title ss-title">
          <span className="ds-header__icon"><CalendarDays size={26} strokeWidth={1.8} /></span>
          <div>
            <h1>Lesson Plan</h1>
            <p>Plan - Teach - Track - Make an Impact</p>
          </div>
          {visit && <span className={`ds-status ds-status--${visit.status}`}>{STATUS_LABEL[visit.status] || visit.status}</span>}
        </header>

        {state.status === "loading" && <Message icon={<RefreshCw size={28} className="ss-spin" />} title="Loading the schedule…" />}

        {state.status === "invalid" && (
          <Message icon={<AlertTriangle size={28} />} title="This link isn't valid">
            <p>It may be incomplete or have been changed. Ask the admin to share the visit again.</p>
          </Message>
        )}

        {state.status === "error" && (
          <Message icon={<AlertTriangle size={28} />} title="Couldn't load the schedule">
            <p>Check your internet connection and try again.</p>
            <button type="button" className="ss-retry" onClick={load}><RefreshCw size={15} />Try again</button>
          </Message>
        )}

        {state.status === "ready" && !visit && (
          <Message icon={<CalendarDays size={28} />} title={`No visit at ${centreName} on ${longDate(date)}`}>
            <p>It may have been moved to another day or removed. Ask the admin for the latest schedule.</p>
          </Message>
        )}

        {visit && (
          <>
            <VisitFields date={date} centreName={centreName} visit={visit} />

            {groups.map((group) => (
              <PlanCard key={group.key} group={group} centreName={centreName} showCoaches={groups.length > 1} date={date} shareKey={shareKey} />
            ))}
          </>
        )}

        {hasLastVisit && (visit || lastVisit) && <LastVisit lastVisit={lastVisit} centreName={centreName} date={date} shareKey={shareKey} />}

        <footer className="ss-footer">Shared from ISF Playground · View only</footer>
      </div>
    </main>
  );
}
